import { Link } from "@tanstack/react-router";
import {
  Loader2,
  Users,
  TrendingUp,
  Target,
  Trophy,
  Megaphone,
  CalendarClock,
} from "lucide-react";
import { useMktDashboard } from "../api";
import { useMarketingStore } from "../store";
import { MarketingHeader } from "../components/MarketingHeader";

function eur(n: number, compact = false): string {
  if (compact && Math.abs(n) >= 1000) {
    return (
      new Intl.NumberFormat("es-ES", { notation: "compact", maximumFractionDigits: 1 }).format(n) +
      " €"
    );
  }
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

const EMBUDO_COLORS = ["#94a3b8", "#38bdf8", "#a78bfa", "#10b981"];

export function MarketingDashboardPage() {
  const { emisorId } = useMarketingStore();
  const { data, isLoading } = useMktDashboard(emisorId);

  return (
    <div className="p-8 flex flex-col gap-6">
      <MarketingHeader title="Marketing" subtitle="Vista general de captación y conversión." />

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-5 shadow-soft">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-black uppercase tracking-wider text-sky-700">
                    Contactos totales
                  </div>
                  <div className="text-3xl font-black text-slate-900 mt-1">
                    {data.kpis.total_contactos}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    +{data.kpis.leads_nuevos_mes} este mes
                  </div>
                </div>
                <div className="rounded-xl p-2.5 bg-sky-100 text-sky-700">
                  <Users size={20} />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-soft">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-black uppercase tracking-wider text-emerald-700">
                    Tasa de conversión
                  </div>
                  <div className="text-3xl font-black text-slate-900 mt-1">
                    {data.kpis.tasa_conversion}%
                  </div>
                  <div className="text-xs text-slate-500 mt-1">contacto → cliente</div>
                </div>
                <div className="rounded-xl p-2.5 bg-emerald-100 text-emerald-700">
                  <TrendingUp size={20} />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 shadow-soft">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-black uppercase tracking-wider text-amber-700">
                    Pipeline abierto
                  </div>
                  <div className="text-3xl font-black text-slate-900 mt-1">
                    {eur(data.kpis.pipeline_abierto, true)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {data.kpis.oportunidades_abiertas} oport. · ponderado{" "}
                    {eur(data.kpis.pipeline_ponderado, true)}
                  </div>
                </div>
                <div className="rounded-xl p-2.5 bg-amber-100 text-amber-700">
                  <Target size={20} />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-brand-700/20 bg-gradient-to-br from-brand-700 to-emerald-600 text-white p-5 shadow-soft">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-black uppercase tracking-wider text-emerald-100">
                    Ganado
                  </div>
                  <div className="text-3xl font-black mt-1">{eur(data.kpis.ganadas_total, true)}</div>
                  <div className="text-xs text-emerald-100/80 mt-1">
                    {data.kpis.ganadas_count} oportunidades
                  </div>
                </div>
                <div className="rounded-xl p-2.5 bg-white/15">
                  <Trophy size={20} />
                </div>
              </div>
            </div>
          </div>

          {/* Embudo + leads por mes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <h2 className="font-black text-slate-900 mb-4">Embudo de conversión</h2>
              <div className="space-y-2">
                {data.embudo.map((e, i) => {
                  const max = Math.max(...data.embudo.map((x) => x.n), 1);
                  const pct = (e.n / max) * 100;
                  return (
                    <div key={e.estado}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-bold text-slate-700">{e.label}</span>
                        <span className="font-mono font-bold">{e.n}</span>
                      </div>
                      <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, background: EMBUDO_COLORS[i] }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <h2 className="font-black text-slate-900 mb-4">Nuevos contactos (6 meses)</h2>
              <div className="flex items-end gap-2 h-40">
                {data.por_mes.map((m) => {
                  const max = Math.max(...data.por_mes.map((x) => x.n), 1);
                  const h = m.n > 0 ? Math.max((m.n / max) * 100, 4) : 0;
                  return (
                    <div key={m.mes} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
                      <span className="text-[10px] font-bold text-slate-400">{m.n || ""}</span>
                      <div
                        className="w-full rounded-t-md bg-gradient-to-t from-brand-700 to-brand-400"
                        style={{ height: `${h}%` }}
                      />
                      <span className="text-[11px] font-bold text-slate-500 capitalize">{m.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Por fuente + top campañas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <h2 className="font-black text-slate-900 mb-4">Leads por fuente</h2>
              {data.por_fuente.length === 0 ? (
                <p className="text-sm text-slate-400">Sin datos.</p>
              ) : (
                <div className="space-y-2">
                  {data.por_fuente.map((f) => {
                    const max = Math.max(...data.por_fuente.map((x) => x.n), 1);
                    return (
                      <div key={f.fuente}>
                        <div className="flex justify-between text-sm mb-0.5">
                          <span className="text-slate-700 truncate">{f.fuente}</span>
                          <span className="font-mono font-bold">{f.n}</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-sky-500"
                            style={{ width: `${(f.n / max) * 100}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-black text-slate-900 flex items-center gap-2">
                  <Megaphone size={18} className="text-brand-700" /> Top campañas (ROI)
                </h2>
                <Link
                  to="/marketing/campaigns"
                  className="text-xs font-bold text-brand-700 hover:text-brand-900"
                >
                  Ver todas →
                </Link>
              </div>
              {data.top_campanas.length === 0 ? (
                <p className="text-sm text-slate-400">Aún no hay campañas.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {data.top_campanas.map((c) => (
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="py-2 font-bold text-slate-800">{c.nombre}</td>
                        <td className="py-2 text-right font-mono text-slate-600">
                          {eur(c.ingresos, true)}
                        </td>
                        <td className="py-2 text-right w-20">
                          {c.roi !== null && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                                c.roi >= 0
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-rose-50 text-rose-700 border border-rose-200"
                              }`}
                            >
                              {c.roi}%
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Contenidos próximos */}
          {data.contenidos_proximos.length > 0 && (
            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <h2 className="font-black text-slate-900 mb-3 flex items-center gap-2">
                <CalendarClock size={18} className="text-brand-700" /> Contenidos programados
              </h2>
              <div className="flex flex-wrap gap-2">
                {data.contenidos_proximos.map((c) => (
                  <div
                    key={c.id}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                  >
                    <div className="font-bold text-slate-800">{c.titulo}</div>
                    <div className="text-xs text-slate-500">
                      {c.canal ?? "—"} ·{" "}
                      {new Date(c.fecha_programada).toLocaleDateString("es-ES")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
